import type { SourceCatalogEntry } from '@shared/api';
import type { ConnectionDto } from '@shared/connections';
import { roleAtLeast } from '@shared/models';
import { useQuery } from '@tanstack/react-query';
import { CircleSlash, PlugZap, ShieldCheck } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/query';
import { useMe } from '../../lib/session';
import { ConnectionControl } from './connection-control';

const STATUS: Record<SourceCatalogEntry['status'], { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  ready: { label: 'Ready', tone: 'success' },
  needs_connection: { label: 'Needs connection', tone: 'warning' },
  unavailable: { label: 'Unavailable', tone: 'neutral' },
};

/** Honest view of what each source can do right now, and what it would need to run. */
export function SourcesPage() {
  const me = useMe();
  const sources = useQuery({
    queryKey: queryKeys.catalogSources,
    queryFn: () => api.get<SourceCatalogEntry[]>('/api/catalog/sources'),
  });
  const connections = useQuery({
    queryKey: queryKeys.connections,
    queryFn: () => api.get<ConnectionDto[]>('/api/connections'),
  });

  const canManage = me.data ? roleAtLeast(me.data.workspace.role, 'admin') : false;
  const isDemo = me.data?.workspace.kind === 'demo';
  const geminiConnection = connections.data?.find((entry) => entry.provider === 'gemini');
  const sheetsConnection = connections.data?.find((entry) => entry.provider === 'google_sheets');
  const gmailConnection = connections.data?.find((entry) => entry.provider === 'gmail');

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <header className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Sources</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          How each source acquires data, and whether it can run in this workspace.
        </p>
      </header>

      {isDemo ? (
        <Alert className="mb-4" tone="warning" title="Demo workspace">
          Sources here run against built-in fixture data so you can try the full pipeline. Nothing is fetched from the live
          internet, and the results are not real businesses.
        </Alert>
      ) : null}

      {sources.isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Checking sources…
        </div>
      ) : sources.isError ? (
        <Alert tone="error">Sources could not be loaded. Reload the page to try again.</Alert>
      ) : (
        <div className="space-y-3">
          {sources.data?.map((source) => {
            const status = STATUS[source.status];
            const connection = source.connection
              ? connections.data?.find((entry) => entry.provider === source.connection)
              : undefined;

            return (
              <Card key={source.id}>
                <CardHeader
                  title={
                    <span className="flex items-center gap-2">
                      {source.label}
                      <Badge tone={status.tone}>{status.label}</Badge>
                      {!source.implemented ? <Badge tone="outline">Not in this build</Badge> : null}
                    </span>
                  }
                  description={source.tagline}
                />
                <CardBody className="space-y-3 text-[13px]">
                  <p className="text-muted-foreground">{source.description}</p>
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <PlugZap className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-medium text-foreground">How data is obtained: </span>
                      {source.acquisition}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-medium text-foreground">Compliance: </span>
                      {source.compliance}
                    </span>
                  </div>

                  {source.message ? (
                    <Alert tone={source.status === 'ready' ? 'info' : 'warning'}>
                      {source.message}
                      {source.action ? <span className="mt-1 block font-medium text-foreground">{source.action}</span> : null}
                    </Alert>
                  ) : null}

                  {source.implemented && source.connection ? (
                    <ConnectionControl provider={source.connection} connection={connection} canManage={canManage} />
                  ) : null}
                </CardBody>
              </Card>
            );
          })}
          {(sources.data?.length ?? 0) === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleSlash className="h-4 w-4" aria-hidden="true" /> No sources are registered.
            </div>
          ) : null}

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  Google Sheets export
                  <Badge tone={sheetsConnection ? 'success' : 'neutral'}>{sheetsConnection ? 'Connected' : 'Not connected'}</Badge>
                </span>
              }
              description="Write results straight into a spreadsheet instead of downloading a file."
            />
            <CardBody className="space-y-3 text-[13px]">
              <p className="text-muted-foreground">
                Exports are written with a Google <span className="font-medium text-foreground">service account</span>, so there
                is no sign-in redirect and this app never gains access to your Google account — only to the individual
                spreadsheets you choose to share with it. Revoke access at any time by un-sharing the sheet or deleting the key
                in Google Cloud.
                {sheetsConnection?.hint ? (
                  <>
                    {' '}
                    Share each sheet with <span className="font-medium text-foreground">{sheetsConnection.hint}</span> (Editor).
                  </>
                ) : null}
              </p>
              <ConnectionControl provider="google_sheets" connection={sheetsConnection} canManage={canManage} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  Outreach sending account
                  <Badge tone={gmailConnection ? 'success' : 'neutral'}>{gmailConnection ? 'Connected' : 'Not connected'}</Badge>
                </span>
              }
              description="The Gmail address tailored outreach emails are sent from."
            />
            <CardBody className="space-y-3 text-[13px]">
              <p className="text-muted-foreground">
                Uses the same Google OAuth client as the 2.0 contact form, with the{' '}
                <span className="font-medium text-foreground">gmail.send</span> scope only — it can send as the address
                you authorise and nothing else. Mail is never sent automatically: every email is drafted, shown to you
                with the facts behind it, and goes out only when you confirm.
                {gmailConnection?.hint ? (
                  <>
                    {' '}
                    Sending as <span className="font-medium text-foreground">{gmailConnection.hint}</span>.
                  </>
                ) : null}
              </p>
              <ConnectionControl provider="gmail" connection={gmailConnection} canManage={canManage} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  AI-answered custom attributes
                  <Badge tone={geminiConnection ? 'success' : 'neutral'}>{geminiConnection ? 'Connected' : 'Not connected'}</Badge>
                </span>
              }
              description="Powers custom questions the deterministic rules can't answer on their own."
            />
            <CardBody className="space-y-3 text-[13px]">
              <p className="text-muted-foreground">
                Hiring, delivery, branch count, multi-city and role lookups are answered from collected data and never need
                this. Everything else is answered by Gemini using only what this research already collected — never general
                knowledge — and is always labelled "AI-inferred" wherever it's shown.
              </p>
              <ConnectionControl provider="gemini" connection={geminiConnection} canManage={canManage} />
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
